import { Box, Chip, Typography } from "@mui/material";
import styled, { keyframes } from "styled-components";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0;  }
`;

/* ---------- header date pill ---------- */

export const DatePill = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 999px;
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  background-color: ${({ theme }) => theme.palette.background.secondary};
  border: 1px solid ${({ theme }) => theme.palette.borderColor.default};
  transition: border-color 0.18s ease, transform 0.18s ease;
  user-select: none;

  &:hover {
    border-color: ${({ theme, $disabled }) =>
      $disabled ? theme.palette.borderColor.default : theme.palette.text.primary};
    transform: ${({ $disabled }) => ($disabled ? "none" : "translateY(-1px)")};
  }
`;

export const DateText = styled(Typography).attrs({ variant: "caption" })`
  font-weight: 600;
  letter-spacing: 0.01em;
  color: ${({ theme }) => theme.palette.text.primary};
`;

/* ---------- timeline ---------- */

export const Timeline = styled(Box)`
  margin-block-start: 20px;
`;

export const Entry = styled(Box)`
  position: relative;
  display: grid;
  grid-template-columns: 104px 1fr;
  gap: 16px;
  align-items: start;
  padding-block: 10px;
  animation: ${fadeUp} 0.4s ease both;
  animation-delay: calc(var(--i, 0) * 45ms);

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

export const YearColumn = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  padding-inline-end: 18px;
  padding-block-start: 14px;

  @media (max-width: 700px) {
    flex-direction: row;
    align-items: baseline;
    gap: 8px;
    padding: 0;
  }
`;

// Digits are always LTR; isolate them so a Hebrew page never reorders them.
export const Year = styled(Typography).attrs({
  dir: "ltr",
  style: { unicodeBidi: "isolate" },
})`
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.palette.text.primary};

  @media (max-width: 700px) {
    font-size: 1.2rem;
  }
`;

export const YearsAgo = styled(Typography).attrs({ variant: "caption" })`
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.secondary};
  white-space: nowrap;
`;

// Done rows drop to reduced emphasis so a scan reads "bright = work left".
export const EntryCard = styled(Box)`
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.palette.borderColor.default};
  background-color: ${({ theme, $done }) =>
    $done ? theme.palette.background.secondary : theme.palette.background.default};
  transition: border-color 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: ${({ theme }) => theme.palette.borderColor.dark};
    box-shadow: 0 4px 14px
      ${({ theme }) =>
        theme.palette.mode === "dark"
          ? "rgba(0, 0, 0, 0.4)"
          : "rgba(0, 0, 0, 0.06)"};
  }
`;

export const Thumb = styled.img`
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 10px;
  flex-shrink: 0;
  opacity: ${({ $muted }) => ($muted ? 0.6 : 1)};
  background-color: ${({ theme }) => theme.palette.background.secondary};
`;

export const ThumbFallback = styled(Box)`
  width: 72px;
  height: 72px;
  border-radius: 10px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ $muted }) => ($muted ? 0.6 : 1)};
  background-color: ${({ theme }) => theme.palette.background.default};
  color: ${({ theme }) => theme.palette.text.disabled};
`;

export const EntryBody = styled(Box)`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// The event text is Hebrew whatever the UI language is, so it carries its own
// direction rather than inheriting the page's.
export const EventText = styled(Typography).attrs({ dir: "rtl" })`
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.5;
  text-align: start;
  opacity: ${({ $muted }) => ($muted ? 0.65 : 1)};
  color: ${({ theme }) => theme.palette.text.primary};
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const MetaRow = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

export const SourceChip = styled(Chip).attrs({ size: "small" })`
  height: 22px;
  font-weight: 600;
  border-radius: 6px;
  background-color: ${({ $bg }) => $bg};
  color: ${({ $fg }) => $fg};
  user-select: none;
  & .MuiChip-label {
    padding-inline: 8px;
    font-size: 0.6875rem;
    letter-spacing: 0.02em;
  }
`;

export const EntryActions = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  align-self: center;
`;

/* ---------- empty state ---------- */

export const EmptyState = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 14px;
  padding: 64px 16px;
`;

export const EmptyOrb = styled(Box)`
  width: 84px;
  height: 84px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.palette.background.secondary};
  border: 1px solid ${({ theme }) => theme.palette.borderColor.default};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.palette.text.disabled};
`;

/* ---------- skeleton ---------- */

export const ShimmerBar = styled(Box)`
  height: 14px;
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.palette.background.secondary} 0%,
    ${({ theme }) => theme.palette.background.dark} 50%,
    ${({ theme }) => theme.palette.background.secondary} 100%
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.6s linear infinite;
`;
